import {Tracker} from './Tracker'
import {Config} from '../Config'
import {ErrorResponse} from './IO/Input/Error/ErrorResponse'
import {ProductClick} from './IO/Input/ProductClick'
import {ProductView} from './IO/Input/ProductView'
import {Transaction} from './IO/Input/Transaction'
import TransactionPayloadPosition from './IO/Input/TransactionPayloadPosition'
import {ProductClickError, ProductViewError, TransactionError} from './IO/Output/Error'
import {AxiosTrackRequest} from './Infrastructure/AxiosRequest'
import {AxiosError} from 'axios'
import {UnknownError} from '../utils/UnknownError'


/**
 * <h4>Tracker implementation</h4>
 *
 * Documentation of Tracker module is in interface description {@link TrackerInterface}
 */
export class TrackerImpl implements Tracker {
    private config: Config;
    private apiRequest: AxiosTrackRequest;

    /**
     * @param config Customer configuration
     */
    constructor(config: Config) {
        this.config = config
        this.apiRequest = new AxiosTrackRequest(config)
    }

    sendProductClick(productClick: ProductClick): void {
        const url = TrackerImpl.getClickUrl(productClick.id)
        this.apiRequest.sendClick(url)
            .catch((reason: AxiosError<ErrorResponse>) => {
                throw new ProductClickError(reason.response?.data.data?.error_message ?? UnknownError.messageByAxiosError(reason))
            })
    }

    sendProductView(productView: ProductView): void {
        const url = this.getViewsUrl(productView.id)
        this.apiRequest.sendView(url)
            .catch((reason: AxiosError<ErrorResponse>) => {
                throw new ProductViewError(reason.response?.data.data?.error_message ?? UnknownError.messageByAxiosError(reason))
            })
    }

    private static getRandomNumber() {
        return Math.floor(Number(new Date()) / 1000 * Math.random())
    }

    getTransactionPayload(transaction: Transaction): URLSearchParams {

        const basket = transaction.basket.map(basketPosition => {
            return <TransactionPayloadPosition>{
                'prod': basketPosition.productId,
                'prc': basketPosition.price,
                'qty': basketPosition.quantity,
                'trn': transaction.transactionId,
                'crn': transaction.currency ?? ''
            }
        })

        return new URLSearchParams([
            ['c', this.config.getCustomer()],
            ['a', 'sell'],
            ['u', ''],
            ['ut', ''],
            ['t', transaction.transactionId],
            ['trn', JSON.stringify(basket)]
        ])

    }


    getTrackingPayload(transaction: Transaction, transactionPosition): URLSearchParams {
        return new URLSearchParams([
            ['c', this.config.getCustomer()],
            ['a', 'purchase'],
            ['u', ''],
            ['ut', ''],
            ['p', transactionPosition.productId],
            ['v', transactionPosition.price],
            ['q', transactionPosition.quantity],
            ['t', transaction.transactionId],
            ['c3', transaction.currency ?? ''],
            ['m', TrackerImpl.getRandomNumber()],
            ['ref', typeof location !== 'undefined' ? location.href : '']
        ])
    }

    sendOnlyTransaction(transaction) {
        const transactionPayload = this.getTransactionPayload(transaction)
        const url = `${this.getTransactionUrl()}?${(transactionPayload)}`
        this.apiRequest.sendTransaction(url)
            .catch((reason: AxiosError<ErrorResponse>) => {
                throw new TransactionError(reason.response?.data.data?.error_message ?? UnknownError.messageByAxiosError(reason))
            })
    }


    sendTransaction(transaction: Transaction): void {
        this.sendOnlyTransaction(transaction)
        this.sendTrackingInfo(transaction)
    }

    private getTransactionUrl(): string {
        const host = this.config.getHost()
        return `https://rec.${host}/trn/`
    }

    private getTrackingUrl(): string {
        const host = this.config.getHost()
        return `https://rec.${host}/t.php`
    }

    private getViewsUrl(productId: string): string {
        const host = this.config.getHost()
        const customerSymbol = this.config.getCustomer()
        const requestParams = new URLSearchParams([
            ['c', customerSymbol],
            ['a', 'view'],
            ['p', productId]
        ])
        return `https://rec.${host}/t.php?${requestParams}`
    }

    private static getClickUrl(id: string) {
        // todo: błędny link zbierający kliki!
        return `https://www.quarticon.com?${id}`
    }

    private sendTrackingInfo(transaction) {
        const basket = transaction.basket
        basket.forEach(transactionPosition => {
            const transactionPositionPayload = this.getTrackingPayload(transaction, transactionPosition)
            const url = `${this.getTrackingUrl()}?${transactionPositionPayload}`

            this.apiRequest.sendTrackingInformation(url)
                .catch((reason: AxiosError<ErrorResponse>) => {
                    throw new TransactionError(reason.response?.data.data?.error_message ?? UnknownError.messageByAxiosError(reason))
                })
        })

    }
}
